import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Search, ChevronRight, ChevronDown, Plus, Volume2, Play, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ---------------- Animated background helpers ---------------- */
const BG_CLASS = {
  chalkboard: 'bg-[radial-gradient(ellipse_at_top,_#0a2540_0%,_#020617_100%)]',
  kitchen:    'bg-[radial-gradient(ellipse_at_top,_#3a2412_0%,_#1a0f06_100%)]',
  grid:       'bg-[radial-gradient(ellipse_at_top,_#0f172a_0%,_#020617_100%)]',
  shop:       'bg-[radial-gradient(ellipse_at_top,_#3a1b47_0%,_#12061a_100%)]',
  graph:      'bg-[radial-gradient(ellipse_at_top,_#082032_0%,_#020617_100%)]',
  dice:       'bg-[radial-gradient(ellipse_at_top,_#4a1a1a_0%,_#170808_100%)]',
  clouds:     'bg-[radial-gradient(ellipse_at_top,_#1e3a5f_0%,_#0a1a2f_100%)]',
  leaves:     'bg-[radial-gradient(ellipse_at_top,_#0f3d2e_0%,_#031a12_100%)]',
  lab:        'bg-[radial-gradient(ellipse_at_top,_#3a1e00_0%,_#170f00_100%)]',
  space:      'bg-[radial-gradient(ellipse_at_top,_#1a0a3a_0%,_#050014_100%)]',
  circuit:    'bg-[radial-gradient(ellipse_at_top,_#0a3a3a_0%,_#00171a_100%)]',
  molecule:   'bg-[radial-gradient(ellipse_at_top,_#3a0a1e_0%,_#170010_100%)]',
  map:        'bg-[radial-gradient(ellipse_at_top,_#3a2900_0%,_#170e00_100%)]',
  book:       'bg-[radial-gradient(ellipse_at_top,_#241a0a_0%,_#0a0700_100%)]',
  clock:      'bg-[radial-gradient(ellipse_at_top,_#1a1a3a_0%,_#050514_100%)]',
};

/* Floating glowing particles for the "eye-catching" background */
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {Array.from({ length: 30 }).map((_, i) => (
      <span
        key={i}
        className="absolute rounded-full bg-white/20 blur-sm animate-pulse"
        style={{
          width: `${6 + Math.random() * 12}px`,
          height: `${6 + Math.random() * 12}px`,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 4}s`,
          animationDuration: `${3 + Math.random() * 5}s`,
        }}
      />
    ))}
  </div>
);

/* Whiteboard-style animated background — drifting formulas + gentle grid. */
const WhiteboardBg = ({ formulas = [] }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-[linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)]" />
    {/* Faint grid like a whiteboard */}
    <div
      className="absolute inset-0 opacity-30"
      style={{
        backgroundImage:
          'linear-gradient(rgba(15,23,42,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,.08) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }}
    />
    {/* Drifting formulas */}
    {formulas.slice(0, 6).map((f, i) => (
      <div
        key={i}
        className="absolute font-mono font-bold text-slate-700/60 select-none"
        style={{
          fontSize: `${20 + (i % 3) * 6}px`,
          top: `${10 + (i * 17) % 70}%`,
          left: `${-20 + (i * 22) % 120}%`,
          animation: `driftX ${18 + i * 3}s linear infinite`,
          animationDelay: `${-i * 4}s`,
          transform: `rotate(${-6 + i * 4}deg)`,
        }}
      >{f}</div>
    ))}
    {/* Rising sparkle bubbles */}
    {Array.from({ length: 14 }).map((_, i) => (
      <span
        key={i}
        className="absolute rounded-full bg-primary/20"
        style={{
          width: `${4 + Math.random() * 10}px`,
          height: `${4 + Math.random() * 10}px`,
          bottom: '-20px', left: `${(i * 7) % 100}%`,
          animation: `riseY ${8 + Math.random() * 8}s linear infinite`,
          animationDelay: `${-Math.random() * 8}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes driftX { from { transform: translateX(0) rotate(-4deg);} to { transform: translateX(120vw) rotate(-4deg);} }
      @keyframes riseY  { 0% { transform: translateY(0); opacity: 0;} 20% { opacity: 1;} 100% { transform: translateY(-110vh); opacity: 0;} }
    `}</style>
  </div>
);

/* Talking-mouth overlay — SVG loops open/close while `talking` is true */
const TalkingMouth = ({ talking }) => (
  <svg viewBox="0 0 40 20" className={`absolute bottom-[26%] left-[47%] w-8 h-4 ${talking ? '' : 'hidden'}`}>
    <ellipse cx="20" cy="10" rx="10" ry="5" fill="#b03040" className="animate-[mouthPulse_0.3s_ease-in-out_infinite]" />
    <style>{`@keyframes mouthPulse{0%,100%{ry:5}50%{ry:2}}`}</style>
  </svg>
);

/* ---------------- Concept Animation ----------------
 * A per-topic on-whiteboard demo — objects that move / interact so the
 * narrator has something concrete to point at. Chooses a visual based on
 * `topic.background` (a small enum) or falls back to drifting formulas.
 */
const NewtonDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-newton">
    {/* Ground */}
    <line x1="20" y1="180" x2="380" y2="180" stroke="#334155" strokeWidth="2" />
    {[...Array(18)].map((_, i) => (
      <line key={i} x1={20 + i * 20} y1="180" x2={30 + i * 20} y2="192" stroke="#334155" strokeWidth="1.5" />
    ))}
    {/* Rolling ball — Newton 1st law: object in motion stays in motion */}
    <circle r="16" fill="#ef4444" stroke="#7f1d1d" strokeWidth="2">
      <animate attributeName="cx" values="30;370;30" dur="6s" repeatCount="indefinite" />
      <animate attributeName="cy" values="164;164;164" dur="6s" repeatCount="indefinite" />
    </circle>
    {/* Push arrow — Newton 2nd law: F = m·a */}
    <g>
      <line x1="0" y1="164" x2="0" y2="164" stroke="#0ea5e9" strokeWidth="4" markerEnd="url(#arrow)">
        <animate attributeName="x1" values="-30;10;-30" dur="6s" repeatCount="indefinite" />
        <animate attributeName="x2" values="10;40;10" dur="6s" repeatCount="indefinite" />
      </line>
      <text fill="#0ea5e9" fontSize="14" fontWeight="700">
        <animate attributeName="x" values="-20;20;-20" dur="6s" repeatCount="indefinite" />
        <animate attributeName="y" values="150;150;150" dur="6s" repeatCount="indefinite" />
        F
      </text>
    </g>
    {/* Cart (reaction pair — Newton 3rd law) */}
    <g>
      <rect y="130" width="60" height="30" fill="#f59e0b" stroke="#78350f" strokeWidth="2" rx="4">
        <animate attributeName="x" values="120;220;120" dur="6s" repeatCount="indefinite" />
      </rect>
      <circle r="6" fill="#111827">
        <animate attributeName="cx" values="130;230;130" dur="6s" repeatCount="indefinite" />
        <animate attributeName="cy" values="164;164;164" dur="6s" repeatCount="indefinite" />
      </circle>
      <circle r="6" fill="#111827">
        <animate attributeName="cx" values="170;270;170" dur="6s" repeatCount="indefinite" />
        <animate attributeName="cy" values="164;164;164" dur="6s" repeatCount="indefinite" />
      </circle>
    </g>
    {/* Reaction arrow (backward) */}
    <line x1="80" y1="140" x2="50" y2="140" stroke="#10b981" strokeWidth="3" markerEnd="url(#arrow)">
      <animate attributeName="x1" values="80;180;80" dur="6s" repeatCount="indefinite" />
      <animate attributeName="x2" values="50;150;50" dur="6s" repeatCount="indefinite" />
    </line>
    <text x="260" y="40" fill="#0f172a" fontSize="15" fontWeight="700">F = m · a</text>
    <text x="260" y="60" fill="#0f172a" fontSize="12">Action ⇌ Reaction</text>
    <defs>
      <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L6,4 L0,8 Z" fill="currentColor" />
      </marker>
    </defs>
  </svg>
);

const PhotosynthesisDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-photosynthesis">
    {/* Sun */}
    <circle cx="60" cy="50" r="24" fill="#fde047" stroke="#ca8a04" strokeWidth="2">
      <animate attributeName="r" values="22;28;22" dur="2.4s" repeatCount="indefinite" />
    </circle>
    {[...Array(8)].map((_, i) => (
      <line key={i} x1="60" y1="50" x2={60 + Math.cos(i * Math.PI / 4) * 40} y2={50 + Math.sin(i * Math.PI / 4) * 40}
            stroke="#fbbf24" strokeWidth="2">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin={`${i * 0.2}s`} repeatCount="indefinite" />
      </line>
    ))}
    {/* Sunlight ray traveling to leaf */}
    <line x1="80" y1="65" x2="200" y2="130" stroke="#fbbf24" strokeWidth="3" strokeDasharray="6 4">
      <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="0.8s" repeatCount="indefinite" />
    </line>
    {/* Plant stem + leaf */}
    <line x1="200" y1="180" x2="200" y2="140" stroke="#166534" strokeWidth="4" />
    <ellipse cx="220" cy="130" rx="30" ry="16" fill="#22c55e" stroke="#14532d" strokeWidth="2" transform="rotate(-20 220 130)" />
    <ellipse cx="180" cy="140" rx="26" ry="14" fill="#4ade80" stroke="#14532d" strokeWidth="2" transform="rotate(20 180 140)" />
    {/* CO2 arrows in */}
    <text x="90" y="150" fill="#0f172a" fontSize="13" fontWeight="700">CO₂
      <animate attributeName="x" values="90;170;90" dur="4s" repeatCount="indefinite" />
    </text>
    {/* Water arrow up */}
    <text x="195" y="200" fill="#0ea5e9" fontSize="13" fontWeight="700">H₂O
      <animate attributeName="y" values="200;150;200" dur="4s" repeatCount="indefinite" />
    </text>
    {/* O2 arrows out */}
    <text x="260" y="130" fill="#059669" fontSize="13" fontWeight="700">O₂
      <animate attributeName="x" values="260;340;260" dur="4s" repeatCount="indefinite" />
    </text>
    {/* Glucose blob */}
    <circle cx="220" cy="130" r="6" fill="#fef08a" stroke="#a16207">
      <animate attributeName="r" values="3;8;3" dur="4s" repeatCount="indefinite" />
    </circle>
    <text x="260" y="50" fill="#0f172a" fontSize="14" fontWeight="700">6CO₂ + 6H₂O ➜ C₆H₁₂O₆ + 6O₂</text>
  </svg>
);

const WaterCycleDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-watercycle">
    <circle cx="70" cy="50" r="22" fill="#fde047" stroke="#ca8a04" strokeWidth="2" />
    <ellipse cx="200" cy="60" rx="50" ry="18" fill="#e2e8f0" stroke="#64748b" strokeWidth="1.5">
      <animate attributeName="cx" values="150;260;150" dur="10s" repeatCount="indefinite" />
    </ellipse>
    <ellipse cx="220" cy="55" rx="40" ry="14" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="1.5">
      <animate attributeName="cx" values="170;280;170" dur="10s" repeatCount="indefinite" />
    </ellipse>
    {/* Ocean */}
    <rect x="20" y="170" width="360" height="30" fill="#38bdf8" stroke="#075985" strokeWidth="1" />
    {/* Evaporation arrows */}
    {[...Array(5)].map((_, i) => (
      <line key={i} x1={60 + i * 30} y1="170" x2={60 + i * 30} y2="90"
            stroke="#0ea5e9" strokeWidth="2" strokeDasharray="4 3" markerEnd="url(#arrow)">
        <animate attributeName="y1" values="170;90;170" dur="4s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0;1" dur="4s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
      </line>
    ))}
    {/* Rain drops */}
    {[...Array(6)].map((_, i) => (
      <circle key={i} cx={200 + i * 15} r="3" fill="#0284c7">
        <animate attributeName="cy" values="80;170;80" dur="2s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;1;1;0" dur="2s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
      </circle>
    ))}
    <text x="30" y="30" fill="#0f172a" fontSize="12" fontWeight="700">☀ evaporation → ☁ condensation → ☔ precipitation</text>
    <defs>
      <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L6,4 L0,8 Z" fill="currentColor" />
      </marker>
    </defs>
  </svg>
);

const PhotoElectricDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-circuit">
    {/* Battery */}
    <rect x="30" y="90" width="20" height="40" fill="#facc15" stroke="#7c2d12" strokeWidth="2" />
    <rect x="50" y="100" width="8" height="20" fill="#facc15" stroke="#7c2d12" strokeWidth="2" />
    {/* Wire circuit */}
    <path d="M60 110 H140 V60 H340 V160 H140 V110" fill="none" stroke="#0f172a" strokeWidth="3" />
    {/* Bulb */}
    <circle cx="240" cy="60" r="16" fill="#fef08a" stroke="#a16207" strokeWidth="2">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite" />
    </circle>
    <text x="260" y="55" fontSize="11" fill="#78350f">bulb glows</text>
    {/* Moving electrons */}
    {[...Array(6)].map((_, i) => (
      <circle key={i} r="4" fill="#3b82f6">
        <animateMotion dur="4s" repeatCount="indefinite" begin={`${i * 0.6}s`}
          path="M60 110 H140 V60 H340 V160 H140 V110 Z" />
      </circle>
    ))}
    <text x="30" y="200" fill="#0f172a" fontSize="12" fontWeight="700">V = I × R  ·  electrons flow − ➜ +</text>
  </svg>
);

const StatesDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-states">
    {/* Solid — molecules packed */}
    <text x="55" y="30" fontSize="12" fontWeight="700" fill="#0f172a">SOLID</text>
    <g>{[...Array(9)].map((_, i) => (
      <circle key={i} cx={30 + (i % 3) * 20} cy={60 + Math.floor(i / 3) * 20} r="7" fill="#60a5fa" stroke="#1e3a8a" />
    ))}</g>
    <rect x="18" y="46" width="70" height="70" fill="none" stroke="#334155" strokeWidth="1.5" />
    {/* Liquid — molecules loosely arranged */}
    <text x="165" y="30" fontSize="12" fontWeight="700" fill="#0f172a">LIQUID</text>
    {[...Array(9)].map((_, i) => (
      <circle key={i} r="7" fill="#38bdf8" stroke="#075985">
        <animate attributeName="cx" values={`${140 + (i * 13) % 70};${150 + (i * 11) % 70};${140 + (i * 13) % 70}`} dur="3s" repeatCount="indefinite" />
        <animate attributeName="cy" values={`${60 + (i * 7) % 60};${80 + (i * 5) % 60};${60 + (i * 7) % 60}`} dur="3s" repeatCount="indefinite" />
      </circle>
    ))}
    <rect x="130" y="46" width="90" height="80" fill="none" stroke="#334155" strokeWidth="1.5" />
    {/* Gas — molecules flying everywhere */}
    <text x="290" y="30" fontSize="12" fontWeight="700" fill="#0f172a">GAS</text>
    {[...Array(8)].map((_, i) => (
      <circle key={i} r="6" fill="#a78bfa" stroke="#4c1d95">
        <animate attributeName="cx" values={`${260 + (i * 17) % 100};${280 + (i * 23) % 100};${260 + (i * 17) % 100}`} dur="2s" repeatCount="indefinite" />
        <animate attributeName="cy" values={`${50 + (i * 11) % 90};${100 + (i * 19) % 90};${50 + (i * 11) % 90}`} dur="2s" repeatCount="indefinite" />
      </circle>
    ))}
    <rect x="255" y="46" width="110" height="90" fill="none" stroke="#334155" strokeWidth="1.5" />
    <text x="60" y="200" fill="#0f172a" fontSize="12">Heat energy loosens the bonds — same molecules, different dress.</text>
  </svg>
);

const OrbitDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-orbit">
    <circle cx="200" cy="110" r="22" fill="#fbbf24" stroke="#78350f" strokeWidth="2" />
    <ellipse cx="200" cy="110" rx="120" ry="60" fill="none" stroke="#64748b" strokeDasharray="4 4" />
    <circle r="9" fill="#3b82f6">
      <animateMotion dur="6s" repeatCount="indefinite">
        <mpath href="#orbit1" />
      </animateMotion>
    </circle>
    <path id="orbit1" d="M 320 110 A 120 60 0 1 1 80 110 A 120 60 0 1 1 320 110" fill="none" stroke="none" />
    <circle r="6" fill="#e2e8f0">
      <animateMotion dur="2.4s" repeatCount="indefinite">
        <mpath href="#orbit2" />
      </animateMotion>
    </circle>
    <path id="orbit2" d="M 260 110 A 60 30 0 1 1 140 110 A 60 30 0 1 1 260 110" fill="none" stroke="none" />
    <text x="40" y="200" fontSize="12" fill="#0f172a" fontWeight="700">Gravity holds planets in orbit around the Sun.</text>
  </svg>
);

const GraphDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-graph">
    <line x1="40" y1="200" x2="380" y2="200" stroke="#334155" strokeWidth="2" />
    <line x1="40" y1="20" x2="40" y2="200" stroke="#334155" strokeWidth="2" />
    {[...Array(8)].map((_, i) => (
      <line key={i} x1={40 + i * 40} y1="200" x2={40 + i * 40} y2="196" stroke="#334155" />
    ))}
    <path d="M40 200 Q 220 -20 380 200" fill="none" stroke="#ef4444" strokeWidth="3">
      <animate attributeName="stroke-dasharray" from="0 800" to="800 0" dur="3s" repeatCount="indefinite" />
    </path>
    <circle r="6" fill="#3b82f6">
      <animateMotion dur="3s" repeatCount="indefinite">
        <mpath href="#curve1" />
      </animateMotion>
    </circle>
    <path id="curve1" d="M40 200 Q 220 -20 380 200" fill="none" stroke="none" />
    <text x="200" y="40" fontSize="13" fontWeight="700" fill="#0f172a">y = a·x² + b·x + c</text>
  </svg>
);

/* ---------- Additional contextual demos ---------- */

/* Pizza — for Fractions */
const PizzaFractionDemo = ({ numerator = 3, denominator = 8 }) => {
  const cx = 200, cy = 110, r = 90;
  const slices = Array.from({ length: denominator }, (_, i) => i);
  const angleStep = (2 * Math.PI) / denominator;
  return (
    <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-pizza">
      <circle cx={cx} cy={cy} r={r + 4} fill="#7c2d12" />
      <circle cx={cx} cy={cy} r={r} fill="#fbbf24" />
      {slices.map(i => {
        const a1 = i * angleStep - Math.PI / 2;
        const a2 = (i + 1) * angleStep - Math.PI / 2;
        const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
        const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
        const large = angleStep > Math.PI ? 1 : 0;
        const isTaken = i < numerator;
        return (
          <g key={i}>
            <path d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`}
                  fill={isTaken ? '#ef4444' : '#fde68a'} stroke="#7c2d12" strokeWidth="1.5">
              {isTaken && (
                <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" begin={`${i * 0.15}s`} repeatCount="indefinite" />
              )}
            </path>
            {isTaken && (
              <>
                <circle cx={cx + (r * 0.55) * Math.cos((a1 + a2) / 2)} cy={cy + (r * 0.55) * Math.sin((a1 + a2) / 2)} r="4" fill="#84cc16" />
                <circle cx={cx + (r * 0.5) * Math.cos((a1 + a2) / 2 + 0.1)} cy={cy + (r * 0.5) * Math.sin((a1 + a2) / 2 + 0.1)} r="3" fill="#facc15" />
              </>
            )}
          </g>
        );
      })}
      <text x="200" y="30" textAnchor="middle" fontSize="16" fontWeight="700" fill="#0f172a">
        {numerator}/{denominator} of the pizza
      </text>
      <text x="200" y="210" textAnchor="middle" fontSize="12" fill="#475569">
        {numerator} slices taken · {denominator - numerator} left · total {denominator} slices
      </text>
    </svg>
  );
};

/* Place value — digit blocks */
const PlaceValueDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-placevalue">
    {/* Number 4275 broken into blocks */}
    {[
      { digit: 4, label: 'Thousands', color: '#ef4444', x: 30, mult: 1000 },
      { digit: 2, label: 'Hundreds', color: '#f59e0b', x: 130, mult: 100 },
      { digit: 7, label: 'Tens', color: '#22c55e', x: 230, mult: 10 },
      { digit: 5, label: 'Ones', color: '#3b82f6', x: 330, mult: 1 },
    ].map(b => (
      <g key={b.label}>
        <rect x={b.x - 30} y="60" width="60" height="90" fill={b.color} stroke="#0f172a" strokeWidth="1.5" rx="6">
          <animate attributeName="y" values="60;50;60" dur="2s" begin={`${(b.mult === 1 ? 0 : 4 - Math.log10(b.mult)) * 0.4}s`} repeatCount="indefinite" />
        </rect>
        <text x={b.x} y="115" textAnchor="middle" fontSize="36" fontWeight="800" fill="white">{b.digit}</text>
        <text x={b.x} y="170" textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">{b.label}</text>
        <text x={b.x} y="185" textAnchor="middle" fontSize="10" fill="#475569">{b.digit} × {b.mult}</text>
      </g>
    ))}
    <text x="200" y="30" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0f172a">
      4275 = 4000 + 200 + 70 + 5
    </text>
    <text x="200" y="210" textAnchor="middle" fontSize="11" fill="#475569">Each digit&apos;s power depends on its POSITION</text>
  </svg>
);

/* Percentage — shopping tag */
const PercentageDemo = () => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-percentage">
    <rect x="70" y="50" width="130" height="140" fill="#0ea5e9" stroke="#0c4a6e" strokeWidth="2" rx="6" />
    <text x="135" y="80" textAnchor="middle" fontSize="12" fill="white">Original</text>
    <text x="135" y="115" textAnchor="middle" fontSize="26" fontWeight="800" fill="white">₹800</text>
    <text x="135" y="140" textAnchor="middle" fontSize="14" fill="white" textDecoration="line-through">MRP</text>
    <text x="135" y="170" textAnchor="middle" fontSize="16" fontWeight="700" fill="#fef08a">40% OFF</text>

    <rect x="230" y="50" width="130" height="140" fill="#22c55e" stroke="#14532d" strokeWidth="2" rx="6">
      <animate attributeName="width" values="130;140;130" dur="2s" repeatCount="indefinite" />
    </rect>
    <text x="295" y="80" textAnchor="middle" fontSize="12" fill="white">You Pay</text>
    <text x="295" y="130" textAnchor="middle" fontSize="34" fontWeight="800" fill="white">₹480</text>
    <text x="295" y="160" textAnchor="middle" fontSize="12" fill="white">Save ₹320</text>

    <line x1="200" y1="120" x2="230" y2="120" stroke="#0f172a" strokeWidth="3" markerEnd="url(#arr-pct)" />
    <defs>
      <marker id="arr-pct" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L6,4 L0,8 Z" fill="#0f172a" />
      </marker>
    </defs>
    <text x="200" y="30" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0f172a">
      40% of ₹800 = ₹320  →  You pay ₹480
    </text>
  </svg>
);

/* Right triangle for Pythagoras / SOH-CAH-TOA */
const RightTriangleDemo = ({ showSquares = true }) => (
  <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-triangle">
    {/* Triangle 3-4-5 scaled */}
    <polygon points="90,180 210,180 90,60" fill="#fbbf24" stroke="#0f172a" strokeWidth="2" />
    {showSquares && (
      <>
        {/* Square on side a (vertical, height 4→ scaled 120) */}
        <rect x="-30" y="60" width="120" height="120" fill="#3b82f6" opacity="0.7" stroke="#0c4a6e" />
        <text x="30" y="130" textAnchor="middle" fontSize="14" fontWeight="700" fill="white">a²</text>
        {/* Square on side b (horizontal, width 3 → scaled 90) */}
        <rect x="90" y="180" width="120" height="90" fill="#22c55e" opacity="0.7" stroke="#14532d" />
        <text x="150" y="220" textAnchor="middle" fontSize="14" fontWeight="700" fill="white">b²</text>
        {/* Square on hypotenuse rotated (approx placement) */}
        <polygon points="210,180 90,60 -18,168 102,288" fill="#ef4444" opacity="0.7" stroke="#7f1d1d" />
      </>
    )}
    {/* Labels */}
    <text x="70" y="125" fontSize="16" fontWeight="700" fill="#0f172a">4</text>
    <text x="145" y="200" fontSize="16" fontWeight="700" fill="#0f172a">3</text>
    <text x="160" y="115" fontSize="16" fontWeight="700" fill="#ef4444">5</text>
    {/* Right-angle marker */}
    <rect x="90" y="170" width="10" height="10" fill="none" stroke="#0f172a" strokeWidth="1.5" />
    <text x="290" y="130" fontSize="14" fontWeight="700" fill="#0f172a">3² + 4² = 5²</text>
    <text x="290" y="150" fontSize="12" fill="#475569">9 + 16 = 25</text>
  </svg>
);

/* Dice for probability */
const DiceDemo = () => {
  const positions = [
    // 1 dot
    [[0.5, 0.5]],
    // 2 dots
    [[0.25, 0.25], [0.75, 0.75]],
    // 3 dots
    [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
    // 4 dots
    [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
    // 5 dots
    [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
    // 6 dots
    [[0.25, 0.2], [0.75, 0.2], [0.25, 0.5], [0.75, 0.5], [0.25, 0.8], [0.75, 0.8]],
  ];
  return (
    <svg viewBox="0 0 400 220" className="w-full h-full" data-testid="demo-dice">
      {[0, 1, 2, 3, 4, 5].map(face => {
        const col = face % 3, row = Math.floor(face / 3);
        const x = 40 + col * 110;
        const y = 40 + row * 90;
        const size = 70;
        const isEven = (face + 1) % 2 === 0;
        return (
          <g key={face}>
            <rect x={x} y={y} width={size} height={size} fill={isEven ? '#22c55e' : '#f8fafc'}
                  stroke="#0f172a" strokeWidth="2" rx="8">
              {isEven && (
                <animate attributeName="stroke-width" values="2;4;2" dur="1.6s" repeatCount="indefinite" />
              )}
            </rect>
            {positions[face].map(([dx, dy], i) => (
              <circle key={i} cx={x + dx * size} cy={y + dy * size} r="5" fill={isEven ? '#052e16' : '#0f172a'} />
            ))}
          </g>
        );
      })}
      <text x="200" y="205" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0f172a">
        P(even) = 3/6 = 1/2 = 50%
      </text>
    </svg>
  );
};

/* Alias exports so DEMO_MAP can point to them */
const FractionsDemo = () => <PizzaFractionDemo numerator={3} denominator={8} />;
const PlaceValueBlocks = () => <PlaceValueDemo />;
const PercentDiscount = () => <PercentageDemo />;
const PythagorasDemo = () => <RightTriangleDemo showSquares={true} />;
const SohCahToaDemo = () => <RightTriangleDemo showSquares={false} />;
const ProbabilityDemo = () => <DiceDemo />;

/* ---------- FANCY topic-specific demos ---------- */

/* DNA — rotating double helix */
const DNAHelixDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-dna">
    <defs>
      <linearGradient id="dna-a" x1="0" x2="1">
        <stop offset="0" stopColor="#3b82f6" />
        <stop offset="1" stopColor="#8b5cf6" />
      </linearGradient>
      <linearGradient id="dna-b" x1="0" x2="1">
        <stop offset="0" stopColor="#ef4444" />
        <stop offset="1" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    {/* Backbone strands as sine curves */}
    {Array.from({ length: 14 }).map((_, i) => {
      const y = 20 + i * 15;
      const phase = i * 0.5;
      return (
        <g key={i}>
          <circle r="7" fill="url(#dna-a)" stroke="#1e293b" strokeWidth="1">
            <animate attributeName="cx" values={`${200 + 60 * Math.sin(phase)};${200 - 60 * Math.sin(phase)};${200 + 60 * Math.sin(phase)}`}
                     dur="4s" repeatCount="indefinite" />
            <animate attributeName="cy" values={`${y};${y};${y}`} dur="4s" repeatCount="indefinite" />
          </circle>
          <circle r="7" fill="url(#dna-b)" stroke="#1e293b" strokeWidth="1">
            <animate attributeName="cx" values={`${200 - 60 * Math.sin(phase)};${200 + 60 * Math.sin(phase)};${200 - 60 * Math.sin(phase)}`}
                     dur="4s" repeatCount="indefinite" />
            <animate attributeName="cy" values={`${y};${y};${y}`} dur="4s" repeatCount="indefinite" />
          </circle>
          {/* Rung — the "base pair" */}
          <line stroke="#64748b" strokeWidth="2">
            <animate attributeName="x1" values={`${200 + 60 * Math.sin(phase)};${200 - 60 * Math.sin(phase)};${200 + 60 * Math.sin(phase)}`} dur="4s" repeatCount="indefinite" />
            <animate attributeName="x2" values={`${200 - 60 * Math.sin(phase)};${200 + 60 * Math.sin(phase)};${200 - 60 * Math.sin(phase)}`} dur="4s" repeatCount="indefinite" />
            <animate attributeName="y1" values={`${y};${y};${y}`} dur="4s" repeatCount="indefinite" />
            <animate attributeName="y2" values={`${y};${y};${y}`} dur="4s" repeatCount="indefinite" />
          </line>
          {/* Base letters */}
          {i % 2 === 0 && (
            <text x={200 + 40 * Math.sin(phase)} y={y + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill="#0f172a">
              {['A', 'T', 'G', 'C'][i % 4]}
            </text>
          )}
        </g>
      );
    })}
    <text x="200" y="15" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">DNA double helix — A-T, G-C</text>
  </svg>
);

/* Chemical reaction — molecules combining (2H₂ + O₂ → 2H₂O) */
const ChemReactionDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-reaction">
    <text x="200" y="20" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0f172a">Chemical Reaction — atoms rearrange</text>

    {/* Hydrogen 1 */}
    <g>
      <circle cx="40" cy="80" r="18" fill="#3b82f6" stroke="#1e3a8a" strokeWidth="2">
        <animate attributeName="cx" values="40;140;200" dur="4s" repeatCount="indefinite" />
        <animate attributeName="cy" values="80;100;110" dur="4s" repeatCount="indefinite" />
      </circle>
      <text x="40" y="85" textAnchor="middle" fontSize="16" fontWeight="700" fill="white" pointerEvents="none">
        <animate attributeName="x" values="40;140;200" dur="4s" repeatCount="indefinite" />
        <animate attributeName="y" values="85;105;115" dur="4s" repeatCount="indefinite" />
        H
      </text>
    </g>
    {/* Hydrogen 2 */}
    <g>
      <circle cx="40" cy="150" r="18" fill="#3b82f6" stroke="#1e3a8a" strokeWidth="2">
        <animate attributeName="cx" values="40;140;220" dur="4s" repeatCount="indefinite" />
        <animate attributeName="cy" values="150;120;130" dur="4s" repeatCount="indefinite" />
      </circle>
      <text x="40" y="155" textAnchor="middle" fontSize="16" fontWeight="700" fill="white" pointerEvents="none">
        <animate attributeName="x" values="40;140;220" dur="4s" repeatCount="indefinite" />
        <animate attributeName="y" values="155;125;135" dur="4s" repeatCount="indefinite" />
        H
      </text>
    </g>
    {/* Oxygen */}
    <g>
      <circle cx="360" cy="120" r="26" fill="#ef4444" stroke="#7f1d1d" strokeWidth="2">
        <animate attributeName="cx" values="360;220;210" dur="4s" repeatCount="indefinite" />
      </circle>
      <text x="360" y="127" textAnchor="middle" fontSize="20" fontWeight="700" fill="white" pointerEvents="none">
        <animate attributeName="x" values="360;220;210" dur="4s" repeatCount="indefinite" />
        O
      </text>
    </g>
    {/* Collision sparks */}
    {Array.from({ length: 6 }).map((_, i) => (
      <circle key={i} cx="215" cy="120" r="0" fill="#facc15">
        <animate attributeName="r" values="0;12;0" dur="1s" begin={`${1.5 + i * 0.08}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;1;0" dur="1s" begin={`${1.5 + i * 0.08}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* Arrow */}
    <line x1="150" y1="200" x2="250" y2="200" stroke="#0f172a" strokeWidth="3" markerEnd="url(#arr-chem)" />
    <text x="200" y="195" textAnchor="middle" fontSize="10" fill="#64748b">react</text>
    <defs>
      <marker id="arr-chem" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L6,4 L0,8 Z" fill="#0f172a" />
      </marker>
    </defs>
    <text x="200" y="225" textAnchor="middle" fontSize="14" fontWeight="700" fill="#065f46">
      2H₂ + O₂ → 2H₂O + energy
    </text>
  </svg>
);

/* Gravitation — falling objects with trails */
const GravitationFallDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-gravity">
    {/* Tower */}
    <rect x="60" y="20" width="30" height="180" fill="#94a3b8" stroke="#334155" strokeWidth="2" />
    <text x="75" y="10" textAnchor="middle" fontSize="9" fill="#0f172a">Tower</text>
    {/* Ground */}
    <line x1="10" y1="220" x2="390" y2="220" stroke="#334155" strokeWidth="2" />
    {[...Array(20)].map((_, i) => (
      <line key={i} x1={10 + i * 20} y1="220" x2={18 + i * 20} y2="232" stroke="#334155" strokeWidth="1" />
    ))}
    {/* Feather — falls slower in air */}
    <g>
      <text x="140" y="35" textAnchor="middle" fontSize="9" fill="#7c2d12">Feather</text>
      <text x="140" fontSize="26">
        <animate attributeName="y" values="40;210;40" dur="4s" repeatCount="indefinite" />
        🪶
      </text>
    </g>
    {/* Ball — falls fast */}
    <g>
      <text x="220" y="35" textAnchor="middle" fontSize="9" fill="#7c2d12">Ball</text>
      <circle r="12" fill="#ef4444" stroke="#7f1d1d" strokeWidth="2">
        <animate attributeName="cy" values="50;210;50" dur="4s" repeatCount="indefinite" keyTimes="0;1;1"
                 keySplines="0.3 0 0.7 1;0 0 0 0" calcMode="spline" />
        <animate attributeName="cx" values="220;220;220" dur="4s" repeatCount="indefinite" />
      </circle>
    </g>
    {/* Hammer */}
    <g>
      <text x="300" y="35" textAnchor="middle" fontSize="9" fill="#7c2d12">Hammer</text>
      <text x="300" fontSize="26">
        <animate attributeName="y" values="50;210;50" dur="4s" repeatCount="indefinite" keyTimes="0;1;1"
                 keySplines="0.3 0 0.7 1;0 0 0 0" calcMode="spline" />
        🔨
      </text>
    </g>
    {/* Formula badge */}
    <g>
      <rect x="20" y="60" width="30" height="12" fill="#fef3c7" stroke="#a16207" rx="2" />
      <text x="35" y="70" textAnchor="middle" fontSize="8" fontWeight="700">g = 9.8</text>
    </g>
    <text x="200" y="14" textAnchor="middle" fontSize="10" fill="#64748b">In a vacuum: ALL objects fall at the same rate</text>
  </svg>
);

/* Circulatory — heart pumping blood */
const CircDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-circ">
    {/* Heart */}
    <path d="M 200 90 C 200 60, 160 60, 160 90 C 160 110, 200 140, 200 140 C 200 140, 240 110, 240 90 C 240 60, 200 60, 200 90 Z"
          fill="#ef4444" stroke="#7f1d1d" strokeWidth="2">
      <animate attributeName="transform" attributeType="XML" type="scale"
               values="1;1.1;1" dur="0.9s" repeatCount="indefinite"
               additive="sum" />
    </path>
    <text x="200" y="105" textAnchor="middle" fontSize="10" fontWeight="700" fill="white">HEART</text>
    {/* Body outline */}
    <ellipse cx="200" cy="140" rx="120" ry="80" fill="none" stroke="#94a3b8" strokeDasharray="4 3" />
    {/* Red blood cells traveling */}
    {Array.from({ length: 8 }).map((_, i) => (
      <circle key={i} r="4" fill="#dc2626">
        <animateMotion dur="4s" repeatCount="indefinite" begin={`${i * 0.5}s`}
          path="M 200,120 C 300,100 320,180 200,190 C 80,180 100,100 200,120 Z" />
      </circle>
    ))}
    {/* Blue return */}
    {Array.from({ length: 5 }).map((_, i) => (
      <circle key={i} r="3.5" fill="#2563eb">
        <animateMotion dur="4s" repeatCount="indefinite" begin={`${i * 0.7 + 1}s`}
          path="M 200,190 C 100,180 80,100 200,120 C 300,100 320,180 200,190 Z" />
      </circle>
    ))}
    <text x="200" y="20" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Circulatory System</text>
    <text x="200" y="225" textAnchor="middle" fontSize="10" fill="#64748b">Red = oxygenated · Blue = deoxygenated</text>
  </svg>
);

/* Ecosystem food chain */
const FoodChainDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-food">
    <text x="200" y="20" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Food chain — Producer → Consumer → Predator</text>
    <g>
      <text x="60" fontSize="34">
        <animate attributeName="y" values="120;115;120" dur="2s" repeatCount="indefinite" />
        🌾
      </text>
      <text x="60" y="155" textAnchor="middle" fontSize="9" fill="#0f172a">Grass</text>
    </g>
    <line x1="100" y1="115" x2="140" y2="115" stroke="#334155" strokeWidth="2" markerEnd="url(#arr-fc)" />
    <g>
      <text x="160" fontSize="34">
        <animate attributeName="y" values="120;115;120" dur="2s" begin="0.4s" repeatCount="indefinite" />
        🐛
      </text>
      <text x="180" y="155" textAnchor="middle" fontSize="9" fill="#0f172a">Insect</text>
    </g>
    <line x1="220" y1="115" x2="260" y2="115" stroke="#334155" strokeWidth="2" markerEnd="url(#arr-fc)" />
    <g>
      <text x="270" fontSize="34">
        <animate attributeName="y" values="120;115;120" dur="2s" begin="0.8s" repeatCount="indefinite" />
        🐦
      </text>
      <text x="290" y="155" textAnchor="middle" fontSize="9" fill="#0f172a">Bird</text>
    </g>
    <line x1="330" y1="115" x2="370" y2="115" stroke="#334155" strokeWidth="2" markerEnd="url(#arr-fc)" />
    <g>
      <text x="378" y="112" fontSize="12">🦅</text>
      <text x="378" y="155" textAnchor="middle" fontSize="9" fill="#0f172a">Eagle</text>
    </g>
    <defs>
      <marker id="arr-fc" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L6,4 L0,8 Z" fill="#334155" />
      </marker>
    </defs>
    <text x="200" y="205" textAnchor="middle" fontSize="10" fill="#64748b">Only 10% of energy passes to the next level</text>
  </svg>
);

/* Atom orbital model */
const AtomDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-atom">
    <circle cx="200" cy="120" r="14" fill="#f59e0b" stroke="#7c2d12" strokeWidth="2" />
    <text x="200" y="125" textAnchor="middle" fontSize="10" fontWeight="700" fill="white">+</text>
    {/* Three orbits at different angles */}
    <ellipse cx="200" cy="120" rx="80" ry="30" fill="none" stroke="#94a3b8" />
    <ellipse cx="200" cy="120" rx="80" ry="30" fill="none" stroke="#94a3b8" transform="rotate(60 200 120)" />
    <ellipse cx="200" cy="120" rx="80" ry="30" fill="none" stroke="#94a3b8" transform="rotate(-60 200 120)" />
    {/* Orbiting electrons */}
    <circle r="6" fill="#3b82f6">
      <animateMotion dur="2s" repeatCount="indefinite">
        <mpath href="#orbA" />
      </animateMotion>
    </circle>
    <path id="orbA" d="M 280 120 A 80 30 0 1 1 120 120 A 80 30 0 1 1 280 120" fill="none" />
    <circle r="6" fill="#22c55e">
      <animateMotion dur="2.5s" repeatCount="indefinite">
        <mpath href="#orbB" />
      </animateMotion>
    </circle>
    <path id="orbB" d="M 240 51 A 80 30 -60 1 1 160 189 A 80 30 -60 1 1 240 51" fill="none" />
    <circle r="6" fill="#ef4444">
      <animateMotion dur="3s" repeatCount="indefinite">
        <mpath href="#orbC" />
      </animateMotion>
    </circle>
    <path id="orbC" d="M 240 189 A 80 30 60 1 1 160 51 A 80 30 60 1 1 240 189" fill="none" />
    <text x="200" y="15" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Bohr&apos;s Atom — electrons in shells</text>
  </svg>
);

/* Solar system */
const SolarSystemDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-solar">
    <rect width="400" height="240" fill="#0f172a" />
    {/* Stars */}
    {[...Array(30)].map((_, i) => (
      <circle key={i} cx={(i * 37) % 400} cy={(i * 53) % 240} r={0.8 + (i % 3) * 0.6} fill="white">
        <animate attributeName="opacity" values="0.3;1;0.3" dur={`${1 + (i % 4)}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* Sun */}
    <circle cx="200" cy="120" r="18" fill="#fbbf24" stroke="#7c2d12">
      <animate attributeName="r" values="16;20;16" dur="2s" repeatCount="indefinite" />
    </circle>
    {/* Orbits */}
    {[35, 55, 78, 105].map((r, i) => (
      <ellipse key={i} cx="200" cy="120" rx={r} ry={r * 0.35} fill="none" stroke="rgba(255,255,255,0.15)" strokeDasharray="2 3" />
    ))}
    {/* Planets */}
    {[
      { path: "M235 120 A 35 12.25 0 1 1 165 120 A 35 12.25 0 1 1 235 120", r: 3, dur: 2, color: '#94a3b8' },
      { path: "M255 120 A 55 19.25 0 1 1 145 120 A 55 19.25 0 1 1 255 120", r: 4, dur: 3, color: '#f97316' },
      { path: "M278 120 A 78 27.3 0 1 1 122 120 A 78 27.3 0 1 1 278 120", r: 5, dur: 4, color: '#3b82f6' },
      { path: "M305 120 A 105 36.75 0 1 1 95 120 A 105 36.75 0 1 1 305 120", r: 4, dur: 6, color: '#dc2626' },
    ].map((p, i) => (
      <g key={i}>
        <circle r={p.r} fill={p.color}>
          <animateMotion dur={`${p.dur}s`} repeatCount="indefinite">
            <mpath href={`#solarpath${i}`} />
          </animateMotion>
        </circle>
        <path id={`solarpath${i}`} d={p.path} fill="none" />
      </g>
    ))}
    <text x="200" y="20" textAnchor="middle" fontSize="12" fontWeight="700" fill="white">Solar System — 8 planets orbit the Sun</text>
  </svg>
);

/* Mitosis — cell splitting */
const MitosisDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-mitosis">
    <text x="200" y="15" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Mitosis — one cell becomes two identical cells</text>
    {/* Parent cell */}
    <g>
      <circle cx="100" cy="130" r="35" fill="#fde68a" stroke="#7c2d12" strokeWidth="2">
        <animate attributeName="opacity" values="1;0" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="90" cy="130" r="8" fill="#dc2626">
        <animate attributeName="opacity" values="1;0" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="110" cy="130" r="8" fill="#dc2626">
        <animate attributeName="opacity" values="1;0" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <text x="100" y="185" textAnchor="middle" fontSize="10" fill="#0f172a">Parent</text>
    </g>
    {/* Splitting */}
    <g>
      <ellipse cx="220" cy="130" rx="45" ry="22" fill="#fde68a" stroke="#7c2d12" strokeWidth="2" opacity="0.6">
        <animate attributeName="rx" values="35;45;35" dur="4s" repeatCount="indefinite" />
      </ellipse>
      <text x="220" y="185" textAnchor="middle" fontSize="10" fill="#0f172a">Dividing</text>
    </g>
    {/* Daughter cells */}
    <g>
      <circle cx="320" cy="110" r="20" fill="#fde68a" stroke="#7c2d12" strokeWidth="2">
        <animate attributeName="opacity" values="0;1" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="320" cy="110" r="6" fill="#dc2626">
        <animate attributeName="opacity" values="0;1" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="320" cy="150" r="20" fill="#fde68a" stroke="#7c2d12" strokeWidth="2">
        <animate attributeName="opacity" values="0;1" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="320" cy="150" r="6" fill="#dc2626">
        <animate attributeName="opacity" values="0;1" dur="4s" begin="1.5s" repeatCount="indefinite" />
      </circle>
      <text x="320" y="200" textAnchor="middle" fontSize="10" fill="#0f172a">2 Daughters</text>
    </g>
    <line x1="140" y1="130" x2="175" y2="130" stroke="#334155" strokeWidth="2" markerEnd="url(#arr-m)" />
    <line x1="265" y1="130" x2="295" y2="130" stroke="#334155" strokeWidth="2" markerEnd="url(#arr-m)" />
    <defs>
      <marker id="arr-m" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L6,4 L0,8 Z" fill="#334155" />
      </marker>
    </defs>
  </svg>
);

/* Devanagari script letters flowing (Hindi/Sanskrit/Marathi topics) */
const ScriptFlowDemo = ({ letters }) => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-script">
    <text x="200" y="20" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Devanagari flow</text>
    {letters.map((l, i) => (
      <text key={i} fontSize="42" fontWeight="700" fill={['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#a855f7'][i % 5]}>
        <animate attributeName="x" values={`-40;${40 + (i * 60)};440`}
                 dur={`${8 + (i % 3) * 2}s`} begin={`${-i * 1.5}s`} repeatCount="indefinite" />
        <animate attributeName="y" values={`${80 + (i % 3) * 40};${80 + (i % 3) * 40}`} dur="10s" repeatCount="indefinite" />
        {l}
      </text>
    ))}
    <text x="200" y="220" textAnchor="middle" fontSize="10" fill="#64748b">प्रत्येक अक्षर एक ध्वनि</text>
  </svg>
);
const HindiScriptDemo = () => <ScriptFlowDemo letters={['अ', 'आ', 'इ', 'क', 'ख', 'ग']} />;
const SanskritScriptDemo = () => <ScriptFlowDemo letters={['ॐ', 'श्री', 'गुरुर्', 'ब्रह्मा', 'सत्यम्']} />;

/* Timeline (History) */
const TimelineDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-timeline">
    <line x1="30" y1="120" x2="370" y2="120" stroke="#334155" strokeWidth="3" />
    {[
      { x: 60,  label: '3000 BCE', text: 'Indus Valley' },
      { x: 160, label: '500 BCE',  text: 'Mauryas' },
      { x: 240, label: '300 CE',   text: 'Guptas' },
      { x: 320, label: '1526 CE',  text: 'Mughals' },
    ].map((e, i) => (
      <g key={i}>
        <circle cx={e.x} cy="120" r="7" fill="#3b82f6" stroke="#1e3a8a" strokeWidth="2">
          <animate attributeName="r" values="6;10;6" dur="2s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
        </circle>
        <line x1={e.x} y1="120" x2={e.x} y2={i % 2 ? "155" : "85"} stroke="#334155" />
        <text x={e.x} y={i % 2 ? 170 : 78} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">{e.label}</text>
        <text x={e.x} y={i % 2 ? 182 : 66} textAnchor="middle" fontSize="9" fill="#64748b">{e.text}</text>
      </g>
    ))}
    {/* Moving pointer */}
    <polygon points="0,110 15,120 0,130" fill="#ef4444">
      <animate attributeName="transform" attributeType="XML" type="translate"
               values="30 0;370 0;30 0" dur="8s" repeatCount="indefinite" />
    </polygon>
    <text x="200" y="20" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Timeline of Indian Empires</text>
  </svg>
);

/* India map with rivers */
const IndiaMapDemo = () => (
  <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="demo-india-map">
    {/* Rough India shape */}
    <path d="M 180 40 L 230 60 L 260 100 L 285 150 L 260 200 L 210 220 L 170 210 L 140 180 L 120 130 L 130 80 L 180 40 Z"
          fill="#fef3c7" stroke="#78350f" strokeWidth="2" />
    {/* Rivers */}
    <path d="M 165 65 Q 200 90 210 130" fill="none" stroke="#3b82f6" strokeWidth="3" strokeDasharray="3 2">
      <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="2s" repeatCount="indefinite" />
    </path>
    <text x="167" y="60" fontSize="9" fill="#1e3a8a">Ganga</text>
    <path d="M 165 100 Q 210 130 220 180" fill="none" stroke="#0284c7" strokeWidth="3" strokeDasharray="3 2">
      <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="2s" repeatCount="indefinite" />
    </path>
    <text x="165" y="95" fontSize="9" fill="#075985">Yamuna</text>
    <path d="M 155 150 Q 200 170 230 190" fill="none" stroke="#22c55e" strokeWidth="3" strokeDasharray="3 2">
      <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="2s" repeatCount="indefinite" />
    </path>
    <text x="140" y="150" fontSize="9" fill="#14532d">Krishna</text>
    <text x="200" y="20" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a">Major rivers of India</text>
  </svg>
);

const DEMO_MAP_EXTRA = {
  // Biology / Botany
  b_dna: DNAHelixDemo,
  b_circulatory: CircDemo,
  b_ecosystem: FoodChainDemo,
  b_cell_division: MitosisDemo,
  b_reproduction: MitosisDemo,
  bo_c3_c4_cam: PhotosynthesisDemo,
  bo_photosynthesis: PhotosynthesisDemo,
  // Chemistry — reactions
  c_stoichiometry: ChemReactionDemo,
  c_chemical_kinetics: ChemReactionDemo,
  c_equilibrium: ChemReactionDemo,
  c_redox: ChemReactionDemo,
  c_atomic_structure: AtomDemo,
  c_periodic_table: AtomDemo,
  c_moles: ChemReactionDemo,
  c_chem_bonding: AtomDemo,
  // Physics — gravity, atom
  p_gravitation: GravitationFallDemo,
  p_units_dimensions: AtomDemo,
  p_bohr_model: AtomDemo,
  p_nuclear: AtomDemo,
  // Geography
  g_solar_system: SolarSystemDemo,
  g_rivers_india: IndiaMapDemo,
  g_mountains_india: IndiaMapDemo,
  g_indian_states: IndiaMapDemo,
  // History
  hi_indus: TimelineDemo,
  hi_mauryan: TimelineDemo,
  hi_gupta: TimelineDemo,
  hi_mughal: TimelineDemo,
  hi_1857: TimelineDemo,
  hi_independence: TimelineDemo,
  // Hindi / Sanskrit / Marathi
  h_varnamala: HindiScriptDemo,
  sa_devanagari: SanskritScriptDemo,
  mr_swaravyanjan: HindiScriptDemo,
};

const DEMO_MAP = {
  // topic.id → component (highest priority) — contextual demos per hot topic
  // Physics
  p_newton_laws: NewtonDemo,
  p_gravitation: OrbitDemo,
  p_shm: GraphDemo,
  p_current_electricity: PhotoElectricDemo,
  p_optics_reflection: PhotoElectricDemo,
  p_optics_refraction: PhotoElectricDemo,
  // Chemistry
  c_matter_states: StatesDemo,
  // Biology / Botany
  b_photosynthesis: PhotosynthesisDemo,
  bo_photosynthesis: PhotosynthesisDemo,
  // Geography
  g_water_cycle: WaterCycleDemo,
  // Math contextual (mapped below via constants)
  m_fractions: FractionsDemo,
  m_place_value: PlaceValueBlocks,
  m_percentage: PercentDiscount,
  m_pythagoras: PythagorasDemo,
  trig_right_triangle: PythagorasDemo,
  trig_sohcahtoa: SohCahToaDemo,
  m_probability: ProbabilityDemo,
  m_quadratic: GraphDemo,
  // Physics extras
  p_projectile: GraphDemo,
  p_circular_motion: OrbitDemo,
  p_photoelectric: PhotoElectricDemo,
  p_electric_field: PhotoElectricDemo,
  // Algebra extras
  alg_quadratic_formula: GraphDemo,
  alg_functions: GraphDemo,
  alg_linear_solve: GraphDemo,
  ...DEMO_MAP_EXTRA,
};
const BG_DEMO_MAP = {
  // topic.background → component (fallback)
  clouds: WaterCycleDemo,
  leaves: PhotosynthesisDemo,
  lab: ChemReactionDemo,
  circuit: PhotoElectricDemo,
  space: OrbitDemo,
  graph: GraphDemo,
  dice: ProbabilityDemo,
  newton: NewtonDemo,
  states: StatesDemo,
  pizza: FractionsDemo,
  placevalue: PlaceValueBlocks,
  shop: PercentDiscount,
  triangle: PythagorasDemo,
  molecule: AtomDemo,
  map: IndiaMapDemo,
  book: TimelineDemo,
  grid: GraphDemo,
  numberline: GraphDemo,
  kitchen: PizzaFractionDemo,
  chalkboard: GraphDemo,
};

const ConceptAnimation = ({ topic }) => {
  const [heroUrl, setHeroUrl] = useState(null);
  const [heroFailed, setHeroFailed] = useState(false);

  useEffect(() => {
    setHeroUrl(null); setHeroFailed(false);
    if (!topic) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/storyteller/topic/${topic.id}/hero`);
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled && j.image_url) setHeroUrl(j.image_url);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [topic?.id]);

  const Demo = DEMO_MAP[topic.id] || BG_DEMO_MAP[topic.background];

  return (
    <div className="w-full h-full flex flex-col items-center gap-3">
      {/* Hero image — FIXED height so it never scales with parent size */}
      <div
        className="relative flex items-start justify-center overflow-hidden shrink-0"
        style={{ height: '360px', width: '100%' }}
      >
        {heroUrl && !heroFailed ? (
          <img
            src={heroUrl}
            alt={topic.title}
            data-testid="topic-hero-image"
            onError={() => setHeroFailed(true)}
            referrerPolicy="no-referrer"
            className="rounded-xl shadow-lg border border-slate-200 bg-white"
            style={{ height: '360px', width: 'auto', maxWidth: '100%', objectFit: 'contain', animation: 'zoomIn 0.6s ease-out both' }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6">
            {topic.formulas && topic.formulas.length > 0 ? (
              <div className="text-4xl md:text-5xl font-mono font-bold text-slate-800 drop-shadow"
                   data-testid="demo-formula-main">
                {topic.formulas[0]}
              </div>
            ) : (
              <div className="text-3xl md:text-4xl font-bold text-slate-600 text-center">
                {topic.title}
              </div>
            )}
            {!heroUrl && !heroFailed && (
              <div className="mt-4 w-12 h-12 rounded-full border-4 border-slate-200 border-t-primary animate-spin" />
            )}
          </div>
        )}
      </div>

      {/* SVG animation strip — shown ONLY when no hero image is available.
          Otherwise it just clutters the whiteboard and (worse) can look off-topic
          if it comes from the generic background fallback. */}
      {Demo && (!heroUrl || heroFailed) && (
        <div
          className="shrink-0 mx-auto"
          style={{ height: '180px', width: '600px', maxWidth: '92%' }}
          data-testid="animation-overlay"
        >
          <Demo />
        </div>
      )}

      <style>{`
        @keyframes zoomIn { from { opacity: 0; transform: scale(0.94);} to { opacity: 1; transform: scale(1);} }
      `}</style>
    </div>
  );
};

/* ---------------- Sidebar ---------------- */
const Sidebar = ({ tree, query, setQuery, open, toggle, onPick, activeId, onRequest }) => {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tree;
    return tree.map(s => ({
      ...s,
      topics: s.topics.filter(t => t.title.toLowerCase().includes(q) || s.subject.toLowerCase().includes(q)),
    })).filter(s => s.topics.length > 0);
  }, [tree, query]);

  return (
    <aside className="w-full h-full flex flex-col overflow-hidden">
      <div className="p-3 border-b border-border sticky top-0 bg-card/60 backdrop-blur z-10 space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search topics…"
            className="pl-9"
            data-testid="storyteller-search"
          />
        </div>
        <Button size="sm" variant="outline" className="w-full" onClick={onRequest} data-testid="storyteller-request-btn">
          <Plus className="w-3 h-3 mr-1" />Request new topic
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map(s => (
          <div key={s.subject} className="border-b border-border/50">
            <button
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-primary/5"
              onClick={() => toggle(s.subject)}
              data-testid={`subject-${s.subject.replace(/\s/g, '-')}`}
            >
              <span className="font-medium text-sm">{s.subject}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">{s.topics.length}</span>
                {open[s.subject] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            </button>
            {(open[s.subject] || query) && s.topics.map(t => (
              <button
                key={t.id}
                onClick={() => onPick(t)}
                className={`w-full text-left px-6 py-1.5 text-xs hover:bg-primary/10 ${activeId === t.id ? 'bg-primary/10 border-l-2 border-primary' : ''}`}
                data-testid={`topic-${t.id}`}
              >
                <div className="font-medium truncate">{t.title}</div>
                <div className="text-[10px] text-muted-foreground">Class {t.class_range}</div>
              </button>
            ))}
          </div>
        ))}

        {/* Bottom filler — subject pill grid so no more dead black space */}
        <div className="p-3 border-t border-border/50 bg-primary/5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Jump to a subject</div>
          <div className="flex flex-wrap gap-1.5">
            {tree.map(s => (
              <button
                key={s.subject}
                onClick={() => toggle(s.subject)}
                className="px-2 py-1 rounded-full text-[10px] bg-background border border-border hover:bg-primary/10"
                data-testid={`subject-jump-${s.subject.replace(/\s/g, '-')}`}
              >
                {s.subject} · {s.topics.length}
              </button>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
            {tree.reduce((n, s) => n + s.topics.length, 0)} struggle-topics across{' '}
            {tree.length} subjects · CBSE · ICSE · SSC · HSC
          </div>
        </div>
      </div>
    </aside>
  );
};

/* ---------------- Narrator Scene ---------------- */
const NarratorScene = ({ topic }) => {
  const [text, setText] = useState('');
  const [talking, setTalking] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    const v = parseInt(localStorage.getItem('cw_font') || '18', 10);
    return isNaN(v) ? 18 : v;
  });
  const audioRef = useRef(null);
  const fetchAbortRef = useRef(null);

  useEffect(() => { localStorage.setItem('cw_font', String(fontSize)); }, [fontSize]);

  // Typewriter reveal of the narration (English only).
  useEffect(() => {
    const narration = topic?.narration || '';
    if (!narration) { setText(''); setTalking(false); return; }
    setText(''); setTalking(true);
    let i = 0;
    const iv = setInterval(() => {
      i += 2;
      setText(narration.slice(0, i));
      if (i >= narration.length) { clearInterval(iv); setTalking(false); }
    }, 25);
    return () => clearInterval(iv);
  }, [topic?.id, topic?.narration]);

  // Auto-start TTS as soon as text begins revealing (immediate speaking).
  useEffect(() => {
    if (!topic) return;
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* noop */ } audioRef.current = null; }
    let cancelled = false;
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoadingAudio(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const url = `${API}/api/storyteller/topic/${topic.id}/audio`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        audioRef.current = new Audio(URL.createObjectURL(blob));
        audioRef.current.onended = () => { setPlayingAudio(false); setTalking(false); };
        setPlayingAudio(true); setTalking(true);
        audioRef.current.play().catch(() => {});
      } catch (_) { /* silent */ }
      finally { if (!cancelled) setLoadingAudio(false); }
    })();
    return () => {
      cancelled = true;
      try { controller.abort(); } catch (_) { /* noop */ }
      if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* noop */ } }
    };
  }, [topic?.id]);

  const listen = async () => {
    if (!topic) return;
    if (audioRef.current) {
      if (audioRef.current.paused) {
        try { await audioRef.current.play(); setPlayingAudio(true); setTalking(true); }
        catch (_) { /* noop */ }
      } else {
        audioRef.current.pause(); setPlayingAudio(false);
      }
      return;
    }
    if (loadingAudio) return;
    try {
      setLoadingAudio(true); setPlayingAudio(true); setTalking(true);
      const token = localStorage.getItem('token');
      const url = `${API}/api/storyteller/topic/${topic.id}/audio`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      audioRef.current = new Audio(objUrl);
      audioRef.current.onended = () => { setPlayingAudio(false); setTalking(false); };
      await audioRef.current.play();
    } catch (e) {
      toast.error('Audio is warming up — please try again in a moment.');
      setPlayingAudio(false); setTalking(false);
    } finally {
      setLoadingAudio(false);
    }
  };

  if (!topic) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground bg-slate-50 dark:bg-slate-900">
        Pick a topic from the left to begin the lesson ✨
      </div>
    );
  }

  // Layout uses a CSS grid: [character column | whiteboard column].
  // Both columns use `alignSelf: start` and their own fixed heights so
  // neither one is stretched by the other. Sidebar toggling, callout font
  // resizing, etc. can never affect the whiteboard geometry.
  return (
    <div className="flex-1 min-w-0 relative overflow-y-auto bg-slate-50" data-testid="narrator-scene">
      <div
        className="w-full grid gap-4 p-4 items-start"
        style={{ gridTemplateColumns: 'minmax(300px, 340px) 1fr' }}
      >
        {/* ─── LEFT: character column — top-aligned, own height, doesn't affect whiteboard ─── */}
        <div className="relative flex flex-col items-center" data-testid="character-column" style={{ alignSelf: 'start' }}>
          {/* Big single caricature — upper body portrait, cropped clean */}
          <div className="relative w-full flex justify-center">
            <div
              className="relative w-full aspect-square rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-xl"
              style={{ maxWidth: '320px' }}
            >
              {topic.avatar_url ? (
                <img
                  src={topic.avatar_url}
                  alt="teacher"
                  data-testid="narrator-avatar"
                  className={`absolute inset-0 w-full h-full object-cover object-top ${talking ? 'animate-[teach_2.4s_ease-in-out_infinite]' : ''}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-7xl">🧑‍🏫</div>
              )}
              {/* Play/pause overlay button */}
              <button
                onClick={listen}
                data-testid="storyteller-listen-btn"
                title={playingAudio ? 'Pause' : 'Listen'}
                className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20"
              >
                <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                  {playingAudio ? <X className="w-6 h-6 text-slate-800" /> : <Volume2 className="w-6 h-6 text-slate-800" />}
                </span>
              </button>
            </div>
          </div>

          {/* Callout BELOW the avatar with tail pointing UP into the character */}
          <div className="relative w-full mt-3" data-testid="callout-bubble" style={{ maxWidth: '320px' }}>
            {/* Upward tail */}
            <div
              className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0"
              style={{
                borderLeft: '12px solid transparent',
                borderRight: '12px solid transparent',
                borderBottom: '14px solid #ffffff',
                filter: 'drop-shadow(0 -1px 0 #cbd5e1)',
              }}
            />
            <div className="bg-white text-slate-900 rounded-2xl p-4 shadow-2xl border border-slate-200">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <h2 className="font-bold leading-snug truncate" style={{ fontSize: `${fontSize + 2}px` }} data-testid="narrator-topic-title">
                    {topic.title}
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {topic.subject} · Class {topic.class_range}
                  </p>
                </div>
                <Button
                  onClick={listen}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 shrink-0"
                  title={playingAudio ? 'Pause' : 'Listen'}
                >
                  {playingAudio ? <X className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
              </div>

              {/* Font-size slider */}
              <div className="flex items-center gap-2 mb-3 text-[10px] text-slate-500">
                <span>Aa</span>
                <input
                  type="range" min="14" max="28" step="1"
                  value={fontSize}
                  onChange={e => setFontSize(parseInt(e.target.value, 10))}
                  className="flex-1 accent-slate-700"
                  data-testid="font-size-slider"
                />
                <span className="text-sm font-bold">Aa</span>
                <span className="tabular-nums w-6 text-right">{fontSize}</span>
              </div>

              <p
                className="leading-relaxed overflow-y-auto pr-1"
                style={{ fontSize: `${fontSize}px`, maxHeight: '38vh' }}
                data-testid="narrator-text"
              >
                {text}<span className="animate-pulse">▎</span>
              </p>
            </div>
          </div>
        </div>

        {/* ─── RIGHT: whiteboard column — FIXED 720px height, independent of everything ─── */}
        <div
          className="relative min-w-0"
          data-testid="whiteboard-column"
          style={{ height: '720px', alignSelf: 'start' }}
        >
          <div className="w-full h-full rounded-2xl border border-slate-200 shadow-xl overflow-hidden bg-white flex flex-col">
            {/* Whiteboard header strip */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 shrink-0">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Whiteboard demo</span>
              </div>
              <span className="text-xs text-slate-500 truncate">{topic.title}</span>
            </div>
            {/* Whiteboard content — locked heights so nothing outside can resize it */}
            <div className="relative flex-1 min-h-0">
              <WhiteboardBg key={`bg-${topic.id}`} formulas={topic.formulas || []} />
              <div className="absolute inset-0 flex flex-col items-center pt-6 pb-4 gap-4">
                {/* Hero + animation card — FIXED size, top-center aligned.
                    key={topic.id} forces a full remount when switching topics
                    so no residue from the previous demo lingers. */}
                <div
                  className="mx-auto"
                  style={{ width: '840px', maxWidth: '92%', height: '560px' }}
                  data-testid="concept-animation-stage"
                >
                  <ConceptAnimation key={topic.id} topic={topic} />
                </div>
                {/* Formulas rail at bottom of whiteboard */}
                {topic.formulas && topic.formulas.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2 justify-center max-w-[88%] shrink-0">
                    {topic.formulas.slice(0, 3).map((f, i) => (
                      <div
                        key={i}
                        className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 font-mono text-sm text-slate-800 shadow-sm"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      >
                        {f}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes teach {
          0%,100% { transform: translateY(0);}
          50%     { transform: translateY(-4px);}
        }
      `}</style>
    </div>
  );
};

/* ---------------- Request modal ---------------- */
const RequestModal = ({ open, subjects, onClose }) => {
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!subject || !title.trim()) return toast.error('Pick a subject and enter a topic title');
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/storyteller/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ subject, title, note }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success('Request submitted — admin will review.');
      onClose(true);
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-background rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold">Request a new topic</h3>
        <div className="space-y-1">
          <Label>Subject</Label>
          <select
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="w-full p-2 rounded-lg border bg-background"
            data-testid="request-subject"
          >
            <option value="">Select subject…</option>
            {subjects.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label>Topic title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Radioactive Decay" data-testid="request-title" />
        </div>
        <div className="space-y-1">
          <Label>Note to admin (optional)</Label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
                    className="w-full h-20 p-2 rounded-lg border text-sm bg-background"
                    placeholder="Why this topic matters…" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="request-submit">
            {busy ? 'Sending…' : 'Send request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function StorytellerPage() {
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [open, setOpen] = useState({});
  const [query, setQuery] = useState('');
  const [topic, setTopic] = useState(null);
  const [showRequest, setShowRequest] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/storyteller/subjects`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include',
        });
        if (res.ok) setTree(await res.json());
      } catch (e) { toast.error('Could not load topics'); }
    })();
  }, []);

  const toggle = (s) => setOpen(o => ({ ...o, [s]: !o[s] }));

  const pickTopic = async (t) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/storyteller/topic/${t.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTopic(await res.json());
    } catch (e) {
      toast.error('Could not load topic');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="max-w-full px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/courses')} data-testid="storyteller-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold leading-tight">Concept Whiteboard</h1>
            <span className="text-xs text-muted-foreground">Struggle-topics for Class 5-12 — explained by teachers on a live whiteboard</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Fixed-width sidebar so the whiteboard NEVER shrinks */}
        <div className="w-72 shrink-0 border-r bg-background flex flex-col overflow-hidden" data-testid="storyteller-sidebar-wrapper">
          <Sidebar
            tree={tree}
            query={query} setQuery={setQuery}
            open={open} toggle={toggle}
            onPick={pickTopic} activeId={topic?.id}
            onRequest={() => setShowRequest(true)}
          />
        </div>
        <NarratorScene topic={topic} />
      </div>

      <RequestModal
        open={showRequest}
        subjects={tree.map(t => t.subject)}
        onClose={() => setShowRequest(false)}
      />
    </div>
  );
}
